<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import MediaLibraryAPI from 'dashboard/api/mediaLibrary';
import MediaLibraryTaxonomy from 'dashboard/components-next/media-library/MediaLibraryTaxonomy.vue';
import MediaLibraryPreview from 'dashboard/components-next/media-library/MediaLibraryPreview.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import MediaLibraryDrawer from 'dashboard/components-next/media-library/MediaLibraryDrawer.vue';
import MediaProductCard from 'dashboard/components-next/media-library/MediaProductCard.vue';
import MediaProductEditor from 'dashboard/components-next/media-library/MediaProductEditor.vue';
import MediaProductDetails from 'dashboard/components-next/media-library/MediaProductDetails.vue';
import MediaLibraryDelete from 'dashboard/components-next/media-library/MediaLibraryDelete.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';

const { t } = useI18n();
const { isAdmin } = useAdmin();
const route = useRoute();
const items = ref([]);
const query = ref('');
const page = ref(1);
const total = ref(0);
const loading = ref(false);
const saving = ref(false);
const notice = ref('');
const error = ref('');
const disabled = ref(false);
const editing = ref(false);
const draft = ref({ title: '', description: '', status: 'approved' });
const upload = ref(null);
const organizing = ref(false);
const taxonomy = ref({ categories: [], products: [], product_classes: [] });
const categoryId = ref('');
const productId = ref('');
const view = ref('products');
const activeProduct = ref(null);
const productTab = ref('media');
const quickProduct = ref(false);
const quickRecord = ref(null);
const productBusy = ref(false);
const newTag = ref('');
const photoSearch = ref('');
const inspected = ref(null);
const drawerTitle = computed(() => {
  if (organizing.value) return t('MEDIA_LIBRARY.ORGANIZE');
  if (editing.value) {
    if (quickProduct.value)
      return t(
        quickRecord.value
          ? 'MEDIA_LIBRARY.EDIT_PRODUCT'
          : 'MEDIA_LIBRARY.ADD_PRODUCT'
      );
    return t(draft.value.id ? 'MEDIA_LIBRARY.EDIT' : 'MEDIA_LIBRARY.ADD');
  }
  return activeProduct.value?.name || t('MEDIA_LIBRARY.FILE_DETAILS');
});
const drawerOpen = computed(
  () =>
    organizing.value || editing.value || activeProduct.value || inspected.value
);
function closeDrawer() {
  if (saving.value || productBusy.value) return;
  organizing.value = false;
  editing.value = false;
  activeProduct.value = null;
  inspected.value = null;
  quickProduct.value = false;
}
function openProduct(product, tab = 'media') {
  closeDrawer();
  activeProduct.value =
    taxonomy.value.products.find(entry => entry.id === product.id) || product;
  productTab.value = tab;
}
function chooseProduct(value) {
  if (value === 'new') {
    quickRecord.value = null;
    quickProduct.value = true;
    return;
  }
  draft.value.product_id = value;
  draft.value.photo_kind = '';
  photoSearch.value = '';
}
function addTag() {
  const values = newTag.value
    .split(',')
    .map(value => value.trim())
    .filter(Boolean);
  draft.value.tags = [...new Set([...(draft.value.tags || []), ...values])];
  newTag.value = '';
}
function inspect(item) {
  closeDrawer();
  inspected.value = item;
}

const filteredProducts = computed(() =>
  taxonomy.value.products.filter(
    product =>
      !categoryId.value || product.category_id === Number(categoryId.value)
  )
);
const draftProduct = computed(() =>
  taxonomy.value.products.find(
    product => product.id === Number(draft.value.product_id)
  )
);
const automaticTitle = computed(
  () =>
    [draftProduct.value?.name, draft.value.photo_kind]
      .filter(Boolean)
      .join(' · ') ||
    (upload.value?.name || draft.value.filename)?.replace(/\.[^.]+$/, '') ||
    t('MEDIA_LIBRARY.UNTITLED')
);
const photoRequirements = computed(
  () =>
    taxonomy.value.product_classes.find(
      entry => entry.id === draftProduct.value?.product_class_id
    )?.photo_requirements || []
);
async function loadTaxonomy() {
  const account = route.params.accountId;
  try {
    const { data } = await MediaLibraryAPI.taxonomy();
    if (account === route.params.accountId) taxonomy.value = data;
  } catch {
    if (account === route.params.accountId)
      error.value = t('MEDIA_LIBRARY.ERROR');
  }
}
let requestId = 0;

async function load() {
  requestId += 1;
  const currentRequest = requestId;
  loading.value = true;
  error.value = '';
  try {
    const { data } = await MediaLibraryAPI.search({
      view: view.value,
      q: query.value,
      page: page.value,
      category_id: categoryId.value || undefined,
      product_id: productId.value || undefined,
    });
    if (currentRequest !== requestId) return;
    items.value = data.items;
    total.value = data.total;
  } catch (e) {
    if (currentRequest !== requestId) return;
    disabled.value = [403, 404].includes(e.response?.status);
    error.value = disabled.value
      ? t('MEDIA_LIBRARY.DISABLED')
      : t('MEDIA_LIBRARY.ERROR');
    items.value = [];
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
}

async function quickSaved(product) {
  await loadTaxonomy();
  draft.value.product_id = product.id;
  quickProduct.value = false;
  load();
}
async function productSaved(product) {
  await loadTaxonomy();
  activeProduct.value =
    taxonomy.value.products.find(entry => entry.id === product.id) || product;
  productTab.value = 'media';
  load();
}
async function deleted() {
  closeDrawer();
  categoryId.value = '';
  productId.value = '';
  page.value = 1;
  await loadTaxonomy();
  await load();
  notice.value = t('MEDIA_LIBRARY.DELETE_SUCCESS');
}
async function taxonomySaved() {
  notice.value = t('MEDIA_LIBRARY.TAXONOMY.SUCCESS');
  await loadTaxonomy();
  await load();
}

function edit(item = null, product = null) {
  closeDrawer();
  draft.value = item
    ? { ...item }
    : {
        title: '',
        description: '',
        status: 'approved',
        product_id: product?.id || '',
        photo_kind: '',
        tags: [],
      };
  draft.value.product_id = draft.value.product_id || '';
  draft.value.tags = [...(draft.value.tags || [])];
  newTag.value = '';
  quickProduct.value = false;
  upload.value = null;
  notice.value = '';
  error.value = '';
  editing.value = true;
}

function statusLabel(status) {
  return {
    pending: t('MEDIA_LIBRARY.STATUS.pending'),
    approved: t('MEDIA_LIBRARY.STATUS.approved'),
    archived: t('MEDIA_LIBRARY.STATUS.archived'),
  }[status];
}

async function save() {
  saving.value = true;
  error.value = '';
  try {
    const {
      id,
      title: manualTitle,
      description,
      status,
      photo_kind: photoKind,
    } = draft.value;
    const product = draft.value.product_id || null;
    const title = manualTitle.trim() || automaticTitle.value.slice(0, 200);
    addTag();
    const tags = draft.value.tags;
    if (id) {
      await MediaLibraryAPI.update(id, {
        title,
        description,
        status,
        product_id: product,
        photo_kind: photoKind || '',
        tags,
      });
    } else {
      const body = new FormData();
      body.append('title', title);
      body.append('description', description);
      body.append('file', upload.value);
      body.append('product_id', product || '');
      body.append('photo_kind', photoKind || '');
      tags.forEach(tag => body.append('tags[]', tag));
      await MediaLibraryAPI.create(body);
    }
    editing.value = false;
    notice.value = t('MEDIA_LIBRARY.SUCCESS');
    page.value = 1;
    await load();
    await loadTaxonomy();
  } catch {
    error.value = t('MEDIA_LIBRARY.ERROR');
  } finally {
    saving.value = false;
  }
}

async function download(item) {
  error.value = '';
  try {
    const { data } = await MediaLibraryAPI.download(item.id);
    const url = URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.download = item.filename;
    link.click();
    URL.revokeObjectURL(url);
  } catch {
    error.value = t('MEDIA_LIBRARY.ERROR');
  }
}

function search() {
  page.value = 1;
  load();
}

function changePage(delta) {
  page.value += delta;
  load();
}

watch(
  () => route.params.accountId,
  () => {
    items.value = [];
    editing.value = false;
    disabled.value = false;
    notice.value = '';
    query.value = '';
    categoryId.value = '';
    productId.value = '';
    taxonomy.value = { categories: [], products: [], product_classes: [] };
    organizing.value = false;
    activeProduct.value = null;
    inspected.value = null;
    quickProduct.value = false;
    loadTaxonomy();
    search();
  }
);
onMounted(() => {
  load();
  loadTaxonomy();
});
</script>

<template>
  <main
    class="flex min-w-0 flex-1 flex-col overflow-y-auto bg-n-surface-1 p-4 md:p-8"
  >
    <div class="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <header class="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 class="m-0 text-xl font-semibold text-n-slate-12">
            {{ t('MEDIA_LIBRARY.TITLE') }}
          </h1>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            {{ t('MEDIA_LIBRARY.DESCRIPTION') }}
          </p>
        </div>
        <div v-if="isAdmin && !disabled" class="flex flex-wrap gap-2">
          <Button
            :label="t('MEDIA_LIBRARY.ORGANIZE')"
            icon="i-lucide-folder-tree"
            variant="outline"
            color="slate"
            @click="
              closeDrawer();
              organizing = true;
            "
          />
          <Button
            :label="t('MEDIA_LIBRARY.ADD')"
            icon="i-lucide-plus"
            @click="edit()"
          />
        </div>
      </header>
      <p
        v-if="error && !drawerOpen"
        role="alert"
        class="m-0 text-sm text-n-ruby-11"
      >
        {{ error }}
      </p>
      <p v-if="notice" role="status" class="m-0 text-sm text-n-slate-12">
        {{ notice }}
      </p>
      <form
        v-if="!disabled"
        role="search"
        class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        @submit.prevent="search"
      >
        <div class="flex flex-wrap items-center justify-between gap-3">
          <div
            class="flex gap-1"
            role="group"
            :aria-label="t('MEDIA_LIBRARY.TITLE')"
          >
            <Button
              v-for="entry in ['products', 'files']"
              :key="entry"
              :label="
                t(
                  entry === 'products'
                    ? 'MEDIA_LIBRARY.PRODUCTS_VIEW'
                    : 'MEDIA_LIBRARY.FILES_VIEW'
                )
              "
              :icon="
                entry === 'products' ? 'i-lucide-package' : 'i-lucide-images'
              "
              :variant="view === entry ? 'faded' : 'ghost'"
              color="slate"
              :aria-pressed="view === entry"
              @click="
                view = entry;
                search();
              "
            />
          </div>
          <div
            class="flex w-full min-w-0 basis-full gap-2 sm:w-auto sm:flex-1 sm:basis-auto sm:max-w-md"
          >
            <label for="media-search" class="sr-only">
              {{ t('MEDIA_LIBRARY.SEARCH') }}
            </label>
            <input
              id="media-search"
              v-model="query"
              type="search"
              :placeholder="t('MEDIA_LIBRARY.SEARCH')"
              class="!mb-0 !h-10 min-w-0 flex-1 !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
            />
            <Button
              type="submit"
              icon="i-lucide-search"
              variant="outline"
              color="slate"
              :aria-label="t('MEDIA_LIBRARY.SEARCH_ACTION')"
              :title="t('MEDIA_LIBRARY.SEARCH_ACTION')"
              :disabled="loading"
            />
          </div>
        </div>
        <div class="grid gap-3 sm:grid-cols-2">
          <ComboBox
            :model-value="categoryId"
            :options="[
              { value: '', label: t('MEDIA_LIBRARY.ALL_CATEGORIES') },
              ...taxonomy.categories.map(entry => ({
                value: entry.id,
                label: entry.name,
              })),
            ]"
            :allow-deselect="false"
            :aria-label="t('MEDIA_LIBRARY.CATEGORY')"
            @update:model-value="
              categoryId = $event;
              productId = '';
              search();
            "
          />
          <ComboBox
            :model-value="productId"
            :options="[
              { value: '', label: t('MEDIA_LIBRARY.ALL_PRODUCTS') },
              ...filteredProducts.map(entry => ({
                value: entry.id,
                label: entry.name,
              })),
            ]"
            :allow-deselect="false"
            :aria-label="t('MEDIA_LIBRARY.PRODUCT')"
            @update:model-value="
              productId = $event;
              search();
            "
          />
        </div>
      </form>
      <p v-if="loading" role="status" class="text-sm text-n-slate-11">
        {{ t('MEDIA_LIBRARY.LOADING') }}
      </p>
      <div
        v-else-if="!items.length && !error"
        class="rounded-xl border border-dashed border-n-weak bg-n-solid-2 p-8 text-center"
      >
        <h2 class="text-base font-medium text-n-slate-12">
          {{ t('MEDIA_LIBRARY.EMPTY') }}
        </h2>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ t('MEDIA_LIBRARY.EMPTY_HELP') }}
        </p>
        <Button
          v-if="isAdmin && view === 'products'"
          icon="i-lucide-plus"
          :label="t('MEDIA_LIBRARY.ADD_PRODUCT')"
          variant="outline"
          @click="organizing = true"
        />
      </div>
      <div
        v-else-if="view === 'products'"
        class="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3"
      >
        <MediaProductCard
          v-for="product in items"
          :key="product.id"
          :product="product"
          :can-edit="isAdmin"
          @open="openProduct(product)"
          @add="edit(null, product)"
          @edit="openProduct(product, 'details')"
        />
      </div>
      <div v-else class="grid items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <article
          v-for="item in items"
          :key="item.id"
          class="flex min-w-0 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <button
            type="button"
            class="rounded-lg border border-n-weak focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
            :aria-label="t('MEDIA_LIBRARY.DETAILS') + ': ' + item.title"
            @click="inspect(item)"
          >
            <MediaLibraryPreview :item="item" class="h-48 w-full !bg-n-field" />
          </button>
          <h2 class="m-0 break-words text-base font-semibold text-n-slate-12">
            {{ item.title }}
          </h2>
          <p class="m-0 text-xs text-n-slate-11">
            {{
              [item.product?.name, item.photo_kind].filter(Boolean).join(' · ')
            }}
          </p>
          <div class="flex flex-wrap gap-1">
            <span
              v-for="tag in item.tags"
              :key="tag"
              class="rounded-md bg-n-blue-2 px-2 py-1 text-xs text-n-blue-11"
              >{{ tag }}
            </span>
          </div>
          <span
            class="w-fit rounded-md px-2 py-1 text-xs"
            :class="
              item.status === 'pending'
                ? 'bg-n-amber-2 text-n-amber-11'
                : 'bg-n-alpha-1 text-n-slate-11'
            "
          >
            {{ statusLabel(item.status) }}
          </span>
          <footer class="mt-auto flex gap-2">
            <Button
              class="flex-1"
              :label="t('MEDIA_LIBRARY.DETAILS')"
              variant="outline"
              icon="i-lucide-eye"
              @click="inspect(item)"
            /><Button
              icon="i-lucide-download"
              variant="outline"
              color="slate"
              :aria-label="t('MEDIA_LIBRARY.DOWNLOAD')"
              :title="t('MEDIA_LIBRARY.DOWNLOAD')"
              @click="download(item)"
            />
          </footer>
        </article>
      </div>
      <nav
        v-if="total > 30"
        class="flex items-center justify-between gap-3"
        :aria-label="t('MEDIA_LIBRARY.PAGE', { page })"
      >
        <Button
          :label="t('MEDIA_LIBRARY.PREVIOUS')"
          :disabled="page === 1 || loading"
          variant="outline"
          @click="changePage(-1)"
        />
        <span class="text-xs text-n-slate-11">
          {{ t('MEDIA_LIBRARY.PAGE', { page }) }}
        </span>
        <Button
          :label="t('MEDIA_LIBRARY.NEXT')"
          :disabled="page * 30 >= total || loading"
          variant="outline"
          @click="changePage(1)"
        />
      </nav>
    </div>
    <MediaLibraryDrawer
      v-if="drawerOpen"
      :title="drawerTitle"
      :busy="saving || productBusy"
      @close="closeDrawer"
    >
      <p v-if="error" role="alert" class="m-4 text-sm text-n-ruby-11">
        {{ error }}
      </p>
      <MediaLibraryDelete
        v-if="
          isAdmin &&
          (activeProduct || inspected || (editing && draft.id && !quickProduct))
        "
        :key="
          activeProduct
            ? 'product-' + activeProduct.id
            : 'media-' + (inspected || draft).id
        "
        class="m-4"
        :name="activeProduct?.name || (inspected || draft).title"
        :help="
          t(
            activeProduct
              ? 'MEDIA_LIBRARY.DELETE_PRODUCT_HELP'
              : 'MEDIA_LIBRARY.DELETE_MEDIA_HELP'
          )
        "
        :action="
          () =>
            activeProduct
              ? MediaLibraryAPI.deleteTaxonomy('products', activeProduct)
              : MediaLibraryAPI.delete((inspected || draft).id)
        "
        :disabled="saving || productBusy"
        @busy="saving = $event"
        @deleted="deleted"
      />
      <MediaLibraryTaxonomy
        v-if="organizing && isAdmin"
        :taxonomy="taxonomy"
        contained
        @saved="taxonomySaved"
        @deleted="deleted"
        @close="closeDrawer"
        @open-product="openProduct"
        @busy="productBusy = $event"
      />
      <MediaProductEditor
        v-else-if="editing && quickProduct"
        :product="quickRecord"
        :taxonomy="taxonomy"
        @saved="quickSaved"
        @cancel="quickProduct = false"
        @busy="productBusy = $event"
      />
      <form
        v-if="editing && isAdmin && !quickProduct"
        class="flex w-full flex-col bg-n-solid-2"
        @submit.prevent="save"
      >
        <fieldset
          :disabled="saving"
          class="m-0 flex min-w-0 flex-col gap-4 border-0 p-4"
        >
          <label
            v-if="!draft.id"
            for="media-file"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.FILE') }}
            <span
              class="relative flex min-h-24 items-center gap-3 rounded-lg border border-dashed border-n-slate-7 bg-n-field p-4 hover:border-n-blue-9 focus-within:outline focus-within:outline-2 focus-within:outline-n-blue-9"
            >
              <span
                :class="upload ? 'i-lucide-file-check' : 'i-lucide-upload'"
                class="size-5 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
              <span
                class="min-w-0 break-words text-sm font-medium text-n-slate-12"
              >
                {{ upload?.name || t('MEDIA_LIBRARY.ADD') }}
              </span>
              <input
                id="media-file"
                type="file"
                :required="!upload"
                accept=".jpg,.jpeg,.png,.webp,.pdf,.txt,.csv,.docx,.xlsx,.pptx,.mp3,.mp4,.ogg,.wav"
                aria-describedby="media-file-help"
                class="absolute inset-0 !m-0 !h-full !w-full cursor-pointer opacity-0"
                @change="upload = $event.target.files[0]"
              />
            </span>
            <span
              id="media-file-help"
              class="text-xs font-normal text-n-slate-11"
            >
              {{ t('MEDIA_LIBRARY.ADMIN_UPLOAD_HELP') }}
            </span>
          </label>
          <label
            for="media-product"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.PRODUCT') }}
            <ComboBox
              :model-value="draft.product_id"
              :options="[
                { value: 'new', label: t('MEDIA_LIBRARY.ADD_PRODUCT') },
                { value: '', label: t('MEDIA_LIBRARY.NO_PRODUCT') },
                ...taxonomy.products.map(product => ({
                  value: product.id,
                  label: product.name,
                })),
              ]"
              :allow-deselect="false"
              :disabled="saving"
              :aria-label="t('MEDIA_LIBRARY.PRODUCT')"
              @update:model-value="chooseProduct"
            />
          </label>
          <div class="flex items-center justify-between gap-2">
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('MEDIA_LIBRARY.NO_PRODUCT_HELP') }}
            </p>
            <Button
              v-if="draftProduct"
              size="sm"
              variant="outline"
              color="slate"
              icon="i-lucide-pencil"
              :label="t('MEDIA_LIBRARY.EDIT_PRODUCT')"
              @click="
                quickRecord = draftProduct;
                quickProduct = true;
              "
            />
          </div>
          <label
            for="media-photo-kind"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.PHOTO_KIND') }}
            <ComboBox
              v-if="photoRequirements.length"
              v-model="draft.photo_kind"
              :options="[
                { value: '', label: t('MEDIA_LIBRARY.NO_PHOTO_KIND') },
                ...[
                  ...new Set(
                    [
                      ...photoRequirements,
                      draft.photo_kind,
                      photoSearch.trim(),
                    ].filter(Boolean)
                  ),
                ].map(value => ({ value, label: value })),
              ]"
              :allow-deselect="false"
              :disabled="saving"
              :aria-label="t('MEDIA_LIBRARY.PHOTO_KIND')"
              @search="photoSearch = $event"
            />
            <input
              v-else
              id="media-photo-kind"
              v-model="draft.photo_kind"
              type="text"
              maxlength="100"
              class="!mb-0 !h-10 !w-full !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
              :placeholder="t('MEDIA_LIBRARY.PHOTO_HINT')"
            />
            <span class="text-xs font-normal text-n-slate-11">
              {{ t('MEDIA_LIBRARY.PHOTO_HELP') }}
            </span>
          </label>
          <label
            for="media-title"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.TITLE_LABEL') }}
            <span class="font-normal">
              {{ t('MEDIA_LIBRARY.AUTO_TITLE_HELP') }}
            </span>
            <input
              id="media-title"
              v-model="draft.title"
              type="text"
              :placeholder="automaticTitle"
              maxlength="200"
              class="!mb-0 !h-10 !w-full !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
            />
          </label>
          <label
            for="media-description"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.DESCRIPTION_LABEL') }}
            <textarea
              id="media-description"
              v-model="draft.description"
              maxlength="2000"
              rows="3"
              class="!mb-0 min-h-20 !w-full !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
            />
          </label>
          <div class="flex flex-col gap-2">
            <label for="media-tags" class="m-0 text-xs text-n-slate-11">
              {{ t('MEDIA_LIBRARY.TAGS') }}
            </label>
            <div class="flex flex-wrap gap-1.5">
              <span
                v-for="tag in draft.tags"
                :key="tag"
                class="flex max-w-full items-center gap-1 rounded-md bg-n-blue-2 pl-2 text-xs text-n-blue-11"
              >
                <span class="break-words">{{ tag }} </span>
                <Button
                  size="xs"
                  icon="i-lucide-x"
                  variant="ghost"
                  :aria-label="t('MEDIA_LIBRARY.REMOVE_TAG', { tag })"
                  @click="
                    draft.tags = draft.tags.filter(value => value !== tag)
                  "
                />
              </span>
            </div>
            <div class="flex gap-2">
              <input
                id="media-tags"
                v-model="newTag"
                type="text"
                maxlength="100"
                class="!mb-0 !h-10 min-w-0 flex-1 !rounded-lg !border-n-weak !bg-n-field !px-3 !py-2 !text-sm !text-n-slate-12"
                :placeholder="t('MEDIA_LIBRARY.NEW_TAG')"
                @keydown.enter.prevent="addTag"
              /><Button
                icon="i-lucide-plus"
                variant="outline"
                :aria-label="t('MEDIA_LIBRARY.ADD_TAG')"
                :title="t('MEDIA_LIBRARY.ADD_TAG')"
                :disabled="!newTag.trim()"
                @click="addTag"
              />
            </div>
            <p class="m-0 text-xs text-n-slate-11">
              {{ t('MEDIA_LIBRARY.TAGS_CREATE_HELP') }}
            </p>
          </div>
          <label
            v-if="draft.id"
            for="media-status"
            class="flex flex-col gap-1.5 text-xs text-n-slate-11"
          >
            {{ t('MEDIA_LIBRARY.STATUS_LABEL') }}
            <ComboBox
              v-model="draft.status"
              :options="
                ['pending', 'approved', 'archived'].map(value => ({
                  value,
                  label: statusLabel(value),
                }))
              "
              :allow-deselect="false"
              :disabled="saving"
              :aria-label="t('MEDIA_LIBRARY.STATUS_LABEL')"
            />
          </label>
        </fieldset>
        <footer class="flex justify-end gap-2 border-t border-n-weak p-4">
          <Button
            type="button"
            variant="outline"
            color="slate"
            :label="t('MEDIA_LIBRARY.CANCEL')"
            :disabled="saving"
            @click="editing = false"
          />
          <Button
            type="submit"
            color="blue"
            icon="i-lucide-save"
            :label="t('MEDIA_LIBRARY.SAVE')"
            :is-loading="saving"
            :disabled="saving || (!draft.id && !upload)"
          />
        </footer>
      </form>

      <MediaProductDetails
        v-if="activeProduct"
        :key="activeProduct.id"
        :inert="saving"
        :product="activeProduct"
        :taxonomy="taxonomy"
        :initial-tab="productTab"
        :can-edit="isAdmin"
        @saved="productSaved"
        @add="edit(null, activeProduct)"
        @edit-media="edit"
        @download="download"
        @busy="productBusy = $event"
      />
      <div v-if="inspected" class="flex flex-col gap-4 p-4">
        <MediaLibraryPreview
          :item="inspected"
          class="h-72 w-full !bg-n-field"
        />
        <h3 class="m-0 break-words text-base font-semibold">
          {{ inspected.title }}
        </h3>
        <p class="m-0 whitespace-pre-wrap break-words text-sm text-n-slate-11">
          {{ inspected.description }}
        </p>
        <p class="m-0 break-all text-xs text-n-slate-11">
          {{ inspected.filename }} ·
          {{
            t('MEDIA_LIBRARY.SIZE', {
              size: (inspected.byte_size / 1048576).toFixed(2),
            })
          }}
        </p>
        <p class="m-0 text-xs text-n-slate-11">
          {{ statusLabel(inspected.status) }}
        </p>
        <Button
          v-if="inspected.product"
          variant="outline"
          icon="i-lucide-package"
          :label="inspected.product.name"
          @click="openProduct(inspected.product)"
        />
        <div class="flex gap-2">
          <Button
            icon="i-lucide-download"
            variant="outline"
            :label="t('MEDIA_LIBRARY.DOWNLOAD')"
            @click="download(inspected)"
          /><Button
            v-if="isAdmin"
            icon="i-lucide-pencil"
            :label="t('MEDIA_LIBRARY.EDIT')"
            @click="edit(inspected)"
          />
        </div>
      </div>
    </MediaLibraryDrawer>
  </main>
</template>
